// ContactOrgchartChip.tsx — der Organigramm-Chip im Kopf der Kontakt-Akte.
//
// Zeigt und pflegt das Organigramm, das der KUNDE uns geschickt hat
// (`Contact.orgchartUrl` / `orgchartName`).
//
// Nicht zu verwechseln mit dem Organigramm, das KMVS selbst zeichnet
// (`OrganigrammPopup`, Knopf bei „Verbindungen zu Firmen"): Jenes entsteht aus
// den erfassten Beziehungen und ist jederzeit neu berechenbar. Dieses hier ist
// ein BELEG — die Aufstellung, die wir bekommen haben. Sie darf von unserer
// Sicht abweichen; genau deshalb wird sie aufgehoben statt abgeleitet.
//
// Aufbau und Optik folgen `ContactAuthorityChip`: Chip in der `.metarow`,
// Aufklapp-Menü als Portal mit kvp-rd-Scrim. Wie dort läuft die Änderung über
// den normalen Formularzustand und wird mit dem Speichern-Knopf geschrieben —
// der Upload selbst geht sofort raus (die Datei liegt danach im Storage), die
// VERKNÜPFUNG mit dem Kontakt erst beim Speichern. Das Menü sagt das an.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";

import { apiUrl } from "../../utils/api";
import { aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/** Menübreite in px — EINZIGE Quelle: Inline-Breite UND Randberechnung. */
const MENU_W = 300;

/** Die abgelegte Datei: Media-Ref plus ursprünglicher Dateiname. */
export interface OrgchartDatei {
  url: string;
  name?: string | null;
}

const UPLOAD_ICON = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 12.5V3.5M6.5 7L10 3.5 13.5 7M4 15.5h12" />
  </svg>
);

const DOKUMENT_ICON = (
  <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 2.5h6L15 6v11.5H5.5z" /><path d="M11.5 2.5V6H15" />
  </svg>
);

const ENTFERNEN_ICON = (
  <svg width="17" height="17" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3.5 5h11M7 5V3.5h4V5M5.5 5l.7 9.5h5.6L12.5 5" />
  </svg>
);

/** Das Symbol im Chip selbst — ein Kästchen mit zwei Kästchen darunter. */
const ORG_ICON = (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinejoin="round">
    <rect x="6.5" y="2" width="5" height="4" rx="1" />
    <rect x="1.5" y="12" width="5" height="4" rx="1" />
    <rect x="11.5" y="12" width="5" height="4" rx="1" />
    <path d="M9 6v3M4 12V9h10v3" strokeLinecap="round" />
  </svg>
);

/**
 * Öffnet eine geschützte API-Ressource in einem neuen Tab.
 *
 * `window.open(url)` ist eine Top-Level-Navigation und läuft NICHT durch den
 * fetch-Wrapper aus `utils/api.ts`, der den Bearer-Token anhängt —
 * `/media/orgcharts/:file` (Scope `contacts:read`) antwortet darauf mit 401.
 * Deshalb derselbe Blob-Weg wie bei den Vollmachten: Bytes authentifiziert
 * holen, `blob:`-URL bauen, die öffnen.
 */
async function oeffneAuthentifiziert(pfad: string): Promise<void> {
  const res = await fetch(apiUrl(pfad));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const geoeffnet = window.open(objectUrl, "_blank", "noopener,noreferrer");
  // Großzügig verzögert freigeben: ein zu frühes Revoke lässt den frisch
  // geöffneten Tab in manchen Browsern leer.
  if (geoeffnet) window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

/**
 * Der Text im Chip. Ohne Datei ist das „Nicht hinterlegt" — bewusst nicht
 * „Keins": Dass uns keins vorliegt, heißt nicht, dass der Kunde keines hat.
 */
export function orgchartChipText(
  url?: string | null,
  name?: string | null,
): string {
  if (!String(url ?? "").trim()) return "Nicht hinterlegt";
  const n = String(name ?? "").trim();
  return n || "Hinterlegt";
}

export interface ContactOrgchartChipProps {
  /** Media-Ref der abgelegten Datei (`Contact.orgchartUrl`). */
  url?: string | null;
  /** Ursprünglicher Dateiname (`Contact.orgchartName`). */
  name?: string | null;
  /** Schreibrecht der Akte (`editable` = `canWrite`). Ohne das nur Lesen. */
  editable?: boolean;
  /** Neuer Stand — landet im Formularzustand, geschrieben wird mit „Speichern". */
  onChange?: (datei: OrgchartDatei | null) => void;
  /** Fehlermeldung an den Aufrufer (Toast). Ohne das bleibt der Fehler in der Konsole. */
  onFehler?: (meldung: string) => void;
}

export function ContactOrgchartChip({
  url,
  name,
  editable,
  onChange,
  onFehler,
}: ContactOrgchartChipProps) {
  const [menuAt, setMenuAt] = React.useState<{ x: number; y: number } | null>(null);
  const [laedtHoch, setLaedtHoch] = React.useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const dateiRef = React.useRef<HTMLInputElement>(null);

  const ref = String(url ?? "").trim();
  const hatDatei = ref !== "";
  const text = orgchartChipText(url, name);
  // Ohne Schreibrecht und ohne Datei gibt es nichts aufzuklappen — dann bleibt
  // es ein statischer Chip wie die Sterne daneben.
  const hatAktionen = hatDatei || !!editable;

  const melde = (meldung: string, fehler: unknown) => {
    console.error(meldung, fehler);
    onFehler?.(meldung);
  };

  const schliesse = () => setMenuAt(null);

  const oeffneMenu = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // Anker ist der Chip selbst — in der Zeile stehen mehrere nebeneinander,
    // jedes Menü geht linksbündig unter dem eigenen Chip auf.
    setMenuAt({
      x: Math.max(8, Math.min(r.left, aktivesFenster().innerWidth - MENU_W - 8)),
      y: r.bottom + 6,
    });
  };

  const oeffneDatei = () => {
    schliesse();
    if (!hatDatei) return;
    void oeffneAuthentifiziert(ref.replace(/^\/+/, "")).catch((e) =>
      melde("Organigramm konnte nicht geöffnet werden.", e),
    );
  };

  const entferne = () => {
    schliesse();
    // Nur die Verknüpfung am Kontakt fällt weg; die Datei im Storage bleibt —
    // dasselbe Verhalten wie beim Ersetzen. Wirksam wird es mit „Speichern".
    onChange?.(null);
  };

  const nimmDatei = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const datei = e.target.files?.[0];
    e.target.value = "";
    if (!datei) return;
    setLaedtHoch(true);
    try {
      const fd = new FormData();
      fd.append("file", datei);
      const res = await fetch(apiUrl("upload/contact-orgchart"), { method: "POST", body: fd });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { url: neueUrl, name: neuerName } = (await res.json()) as { url: string; name?: string };
      onChange?.({ url: neueUrl, name: neuerName ?? null });
      // Menü zu, damit der Chip mit dem neuen Stand wieder frei liegt.
      schliesse();
    } catch (err) {
      melde("Organigramm konnte nicht hochgeladen werden.", err);
    } finally {
      setLaedtHoch(false);
    }
  };

  const inhalt = (
    <>
      {ORG_ICON}
      <span className="k">Organigramm:</span>
      <span className="authchip-dateiname">{text}</span>
      {hatAktionen && <span className="statuspill-caret">▾</span>}
    </>
  );

  if (!hatAktionen) {
    return (
      <span className="mchip static" title={`Organigramm des Kunden: ${text}`}>
        {inhalt}
      </span>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="mchip mchip-btn"
        onClick={oeffneMenu}
        aria-haspopup="menu"
        title={`Organigramm des Kunden: ${text}`}
      >
        {inhalt}
      </button>

      {menuAt &&
        createPortal(
          <div className="kvp-rd-scrim statuspill-scrim" onClick={schliesse}>
            <div
              className="kvp-rd statuspill-menu authchip-menu"
              style={{ left: menuAt.x, top: menuAt.y, width: MENU_W }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.stopPropagation();
                  schliesse();
                }
              }}
            >
              <div className="authchip-kopf">
                <span className="authchip-titel">Organigramm des Kunden</span>
                <span className="authchip-hinweis">
                  {editable
                    ? "Änderung wird mit „Speichern“ übernommen"
                    : "Kein Schreibrecht — nur ansehen"}
                </span>
              </div>

              {hatDatei && (
                <button
                  type="button"
                  role="menuitem"
                  className="statuspill-item authchip-item"
                  onClick={oeffneDatei}
                >
                  {DOKUMENT_ICON}
                  <span className="authchip-dateiname">{name || "Organigramm öffnen"}</span>
                </button>
              )}

              {editable && (
                <button
                  type="button"
                  role="menuitem"
                  className="statuspill-item authchip-item"
                  disabled={laedtHoch}
                  onClick={() => dateiRef.current?.click()}
                >
                  {UPLOAD_ICON}
                  {laedtHoch
                    ? "Lädt hoch …"
                    : hatDatei
                      ? "Anderes Organigramm hochladen"
                      : "Organigramm hochladen"}
                </button>
              )}

              {editable && hatDatei && (
                <>
                  <div className="authchip-trenner" />
                  <button
                    type="button"
                    role="menuitem"
                    className="statuspill-item authchip-item"
                    onClick={entferne}
                  >
                    {ENTFERNEN_ICON}
                    Aus der Akte entfernen
                  </button>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}

      {editable && (
        <input
          ref={dateiRef}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: "none" }}
          aria-label="Organigramm des Kunden hochladen"
          onChange={(e) => void nimmDatei(e)}
        />
      )}
    </>
  );
}
